import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { QueryErrorBanner, KpiStripSkeleton, TableSkeletonRows } from "../../components/feedback/QueryFeedback";
import KeyContactsPanel from "../../components/contacts/KeyContactsPanel";
import { contactsApi, eventsApi } from "../../api";
import { workspaceNav } from "../../routes/workspaceNavigation";
import { useAuth } from "../../context/AuthContext";
import { queryKeys } from "../../queryKeys";
import type { EventListItem } from "../../types";
import { formatRelativeUpdated } from "../../utils/format";
import { isDemoSeeded, seedDemoWorkspace } from "../../utils/demoSeed";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import "./HomePage.css";

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

type Props = {
  workspace: WorkspaceId;
};

const HomePage: React.FC<Props> = ({ workspace }) => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [demoSeeded, setDemoSeeded] = useState(() => isDemoSeeded());
  const eventsPath = workspaceNav.events(workspace);
  const newEventPath = workspaceNav.newEvent(workspace);
  const contactsPath = workspaceNav.contacts(workspace);

  const eventsQuery = useQuery({
    queryKey: token ? queryKeys.events.list(workspace) : queryKeys.events.all(workspace),
    queryFn: () => eventsApi.list(token!),
    enabled: Boolean(token),
  });

  const contactsQuery = useQuery({
    queryKey: token ? queryKeys.contacts.list(workspace) : queryKeys.contacts.all(workspace),
    queryFn: () => contactsApi.list(token!),
    enabled: Boolean(token),
  });

  const events = useMemo<EventListItem[]>(() => {
    const data = eventsQuery.data;
    return Array.isArray(data) ? data : [];
  }, [eventsQuery.data]);
  const loading = eventsQuery.isPending;
  const errorMsg = eventsQuery.error instanceof Error ? eventsQuery.error.message : eventsQuery.error ? String(eventsQuery.error) : "";

  const stats = useMemo(() => {
    const now = new Date();
    const total = events.length;
    const totalCap = events.reduce((s, e) => s + (e.venue_capacity || 0), 0);
    const upcoming = events.filter((e) => {
      const d = new Date(e.event_date);
      return !Number.isNaN(d.getTime()) && d >= new Date(now.getFullYear(), now.getMonth(), now.getDate());
    }).length;
    const sorted = [...events].sort((a, b) => new Date(b.event_date).getTime() - new Date(a.event_date).getTime());
    return { total, totalCap, upcoming, recent: sorted.slice(0, 10) };
  }, [events]);

  const displayName = user?.full_name?.trim() || user?.email || "Operator";
  const syncedAt = eventsQuery.dataUpdatedAt ? new Date(eventsQuery.dataUpdatedAt) : null;

  return (
    <div className="app-shell-dash home-page">
      <div className="app-shell-dash-toolbar">
        <div>
          <h1 className="app-shell-page-title home-page__title">
            Your events
          </h1>
          <p className="app-shell-lede home-page__lede">
            {displayName} — portfolio snapshot and quick routes into calendar, events, and finance.
          </p>
          <div className="app-shell-dash-toolbar-meta">
            {syncedAt ? (
              <span className="app-shell-pill">{formatRelativeUpdated(syncedAt)}</span>
            ) : null}
            {eventsQuery.isFetching && !loading ? <span className="app-shell-pill">Refreshing…</span> : null}
            <span className="app-shell-pill app-shell-pill--accent">{stats.total} events</span>
          </div>
        </div>
        <div className="app-shell-refresh-row">
          <button type="button" className="eventos-nav-btn" onClick={() => void eventsQuery.refetch()} disabled={!token || eventsQuery.isFetching}>
            {eventsQuery.isFetching ? "Refreshing…" : "Refresh data"}
          </button>
          {!demoSeeded ? (
            <button
              type="button"
              className="eventos-nav-btn"
              onClick={() => {
                const { savedViewName } = seedDemoWorkspace();
                setDemoSeeded(true);
                toast.success(`Demo view "${savedViewName}" added to calendar`);
                navigate(workspaceNav.calendar(workspace));
              }}
            >
              Load demo preferences
            </button>
          ) : null}
          <Link to={newEventPath} className="app-shell-add-event">
            Add Event
          </Link>
        </div>
      </div>

      {eventsQuery.isError ? (
        <QueryErrorBanner message={errorMsg || "Request failed"} onRetry={() => void eventsQuery.refetch()} retrying={eventsQuery.isFetching} />
      ) : null}

      {loading ? (
        <KpiStripSkeleton count={4} />
      ) : (
        <div className="app-shell-kpi-strip">
          <div className="app-shell-kpi">
            <span className="app-shell-kpi-label">Portfolio</span>
            <span className="app-shell-kpi-value">{stats.total}</span>
            <span className="app-shell-kpi-sub">Total events</span>
          </div>
          <div className="app-shell-kpi">
            <span className="app-shell-kpi-label">Upcoming</span>
            <span className="app-shell-kpi-value">{stats.upcoming}</span>
            <span className="app-shell-kpi-sub">Dated today or later</span>
          </div>
          <div className="app-shell-kpi">
            <span className="app-shell-kpi-label">Seat envelope</span>
            <span className="app-shell-kpi-value">{stats.totalCap.toLocaleString("en-IN")}</span>
            <span className="app-shell-kpi-sub">Σ venue capacity</span>
          </div>
          <div className="app-shell-kpi">
            <span className="app-shell-kpi-label">Workspace</span>
            <span className="app-shell-kpi-value home-page__workspace-kpi">Event operations</span>
            <span className="app-shell-kpi-sub">Calendar · Events · Finance</span>
          </div>
        </div>
      )}

      <KeyContactsPanel
        contacts={contactsQuery.data ?? []}
        contactsPath={contactsPath}
        loading={contactsQuery.isPending}
        title="Key contacts"
        emptyMessage="No contacts yet. Add agents and managers for quick call and email from home."
      />

      <div className="app-shell-dash-panel">
        <div className="app-shell-dash-panel-head">
          <h2 className="app-shell-dash-panel-title">Event roster</h2>
          <Link to={eventsPath} className="app-shell-inline-link home-page__registry-link">
            View all events →
          </Link>
        </div>
        <div className="app-shell-dash-panel-body">
          <div className="app-shell-table-wrap">
            <table className="app-shell-table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>City</th>
                  <th>Date</th>
                  <th>Cap.</th>
                  <th>Status</th>
                  <th className="home-page__actions-head">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeletonRows rows={6} cols={6} />
                ) : eventsQuery.isError ? (
                  <tr>
                    <td colSpan={6} className="home-page__empty-cell">
                      Roster unavailable. Use Retry above.
                    </td>
                  </tr>
                ) : stats.recent.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="home-page__empty-cell">
                      No events yet.{" "}
                      <Link to={newEventPath} className="app-shell-inline-link">
                        Create one
                      </Link>{" "}
                      or open{" "}
                      <Link to={eventsPath} className="app-shell-inline-link">
                        Events
                      </Link>
                      .
                    </td>
                  </tr>
                ) : (
                  stats.recent.map((e) => (
                    <tr key={e.id}>
                      <td>
                        <strong className="home-page__event-name">{e.name}</strong>
                        {e.artist ? <div className="eventos-subtle home-page__event-artist">{e.artist}</div> : null}
                      </td>
                      <td>{e.city}</td>
                      <td>{formatShortDate(e.event_date)}</td>
                      <td>{e.venue_capacity.toLocaleString("en-IN")}</td>
                      <td>
                        <span className="app-shell-pill home-page__status-pill">
                          {e.status}
                        </span>
                      </td>
                      <td>
                        <div className="app-shell-table-actions">
                          <Link to={workspaceNav.dashboard(workspace, e.id)} className="app-shell-inline-link">
                            Open event
                          </Link>
                          <Link to={workspaceNav.eventLive(workspace, e.id)} className="eventos-subtle home-page__workspace-entry">
                            Live Ops
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
